import * as React from 'react';
import { Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { initializeDocumentTypes } from './services/documentTypes';
import { AIModelProvider } from './contexts/AIModelContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { useTranslation } from 'react-i18next';

// Import the actual components
import Login from './pages/Login';
// import Register from './pages/Register';
import PatientList from './pages/PatientList';
import PatientDetail from './pages/PatientDetail';
import MedicalResearch from './pages/MedicalResearch';
import ReportGeneration from './pages/ReportGeneration';
import LandingPage from './pages/LandingPage';
import EnglishLandingPage from './pages/EnglishLandingPage';
import HomeCarePage from './pages/HomeCarePage';
import HomeCarePageChinese from './pages/HomeCarePageChinese';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PrivacyPolicyChinese from './pages/PrivacyPolicyChinese';
import JoinUs from './pages/JoinUs';
import Contact from './pages/Contact';
import AboutUs from './pages/AboutUs';
import Terms from './pages/Terms';
import Security from './pages/Security';
import DashboardLayout from './components/Layout';

// Layout component
const Layout: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-100">
      <main className="max-w-full mx-auto min-h-[calc(100vh)]">
        <Outlet />
      </main>
    </div>
  );
};

// Protected route component
const ProtectedRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();
  
  // Check if the token exists in localStorage as a fallback
  const token = localStorage.getItem('token');
  const hasToken = !!token;
  
  if (!isAuthenticated && !hasToken) {
    return <Navigate to="/login" replace />;
  }
  
  return <Outlet />;
};

// Privacy Policy Route component
const PrivacyPolicyRoute: React.FC = () => {
  const { i18n } = useTranslation();
  return i18n.language === 'zh' ? <PrivacyPolicyChinese /> : <PrivacyPolicy />;
};

// Main App component
const App: React.FC = () => {
  // Initialize document types when the application starts
  React.useEffect(() => {
    initializeDocumentTypes().catch(error => {
      console.error('Failed to initialize document types:', error);
    });
  }, []);

  return (
    <LanguageProvider>
      <AIModelProvider>
        <Routes>
          {/* Public routes */}
          <Route element={<Layout />}>
            <Route path="/" element={<EnglishLandingPage />} />
            <Route path="/cn" element={<LandingPage />} />
            <Route path="/en" element={<EnglishLandingPage />} />
            <Route path="/home-care" element={<HomeCarePage />} />
            <Route path="/home-care-cn" element={<HomeCarePageChinese />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Login />} />
            <Route path="/join-us" element={<JoinUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyRoute />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/careers" element={<JoinUs />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/security" element={<Security />} />
            
            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<PatientList />} />
                <Route path="/patient/:id" element={<PatientDetail />} />
                <Route path="/research" element={<MedicalResearch />} />
                <Route path="/reports" element={<ReportGeneration />} />
              </Route>
            </Route>
          </Route>
          
          {/* Redirect any unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AIModelProvider>
    </LanguageProvider>
  );
};

export default App;